"use client";

import Button from "@/components/atoms/buttons/Button";
import RadioButton from "@/components/atoms/radio-buttons";
import RadioShipping from "@/components/atoms/radio-shipping";
import { toast } from "@/components/atoms/toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCustomer } from "@/hooks/useCustomer";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { FIXED_DELIVERY_HOURS, normalizeDeliverySchedule } from "@/lib/fixed-delivery-schedule";
import { sdk } from "@/lib/medusa/client";
import { getQueryClient } from "@/lib/query";
import { cn, getAddressMetadata, getDifferentAddress } from "@/lib/utils";
import { createAccountLocalisedSchema, ShippingAddressSchema } from "@/lib/validation/account";
import { companyAddressesQueryKey } from "@/queries/company";
import { DeliveryAddress } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { StoreCustomer } from "@medusajs/types";
import { ChevronLeft, Info, Loader2 } from "lucide-react";
import { RefObject, useRef, useState, useTransition } from "react";
import { useForm, type FieldPath } from "react-hook-form";

type ShippingAddressFormProps = {
  onSuccess: () => void;
  addressToEdit?: DeliveryAddress | null;
  addressId?: string;
  locale: Locales;
  addressSource?: "customer" | "company";
  companyId?: string | null;
};

const deliveryLocationOptions = [
  { value: "house", label: "House" },
  { value: "apartment", label: "Apartment" },
  { value: "office", label: "Office / company" },
  { value: "venue", label: "Event venue" },
  { value: "warehouse", label: "Warehouse" },
  { value: "school", label: "School" },
];

const parkingOptions = [
  { value: "front", title: "Right in front" },
  { value: "private_parking", title: "Private parking" },
  { value: "loading_zone", title: "Loading zone" },
  { value: "complicated", title: "Complicated parking" },
  { value: "special_case", title: "Special case" },
];

const flowSteps = [
  {
    title: "Where should we deliver?",
    fields: ["deliveryLocationType", "addressName", "postalAddress", "postalCode", "city", "countryCode"],
  },
  {
    title: "Access to the venue",
    fields: [
      "deliveryHasElevator",
      "deliveryElevatorDimensions",
      "deliveryElevatorDoorWidth",
      "deliveryHasStairs",
      "deliveryStairsCount",
      "deliveryHasGroundLevelAccess",
      "deliveryAccessDetails",
    ],
  },
  { title: "Parking", fields: ["deliveryParkingType", "deliveryParkingDetails"] },
  { title: "Delivery schedule", fields: [] },
  { title: "On-site contact", fields: ["deliveryContactName", "deliveryContactPhone"] },
  { title: "Other remarks", fields: ["deliveryFloor", "deliveryInstructions", "message"] },
] satisfies { title: string; fields: FieldPath<ShippingAddressSchema>[] }[];

const inputClassName =
  "border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-12 placeholder:text-sm focus-visible:ring-[2px]";

const textAreaClassName =
  "border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light min-h-28 resize-none bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]";

const toBoolean = (value?: string) => (value === "yes" ? true : value === "no" ? false : undefined);
const fromBoolean = (value?: boolean) => (typeof value === "boolean" ? (value ? "yes" : "no") : undefined);

const ShippingAddressForm = ({
  onSuccess,
  addressToEdit,
  addressId,
  locale,
  addressSource = "customer",
  companyId,
}: ShippingAddressFormProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPending, startTransition] = useTransition();
  const { setCustomer, customer } = useCustomer();
  const queryClient = getQueryClient();
  const metadata = getAddressMetadata(addressToEdit);
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(dropdownRef as RefObject<HTMLDivElement>, () => setShowDropdown(false));

  const { shippingAddressSchema } = createAccountLocalisedSchema(locale);

  const form = useForm<ShippingAddressSchema>({
    resolver: zodResolver(shippingAddressSchema),
    defaultValues: {
      addressName: addressToEdit?.address_name || metadata.address_name || "",
      postalCode: addressToEdit?.postal_code || "",
      city: addressToEdit?.city || "",
      postalAddress: addressToEdit?.address_1 || "",
      message: metadata.message || "",
      countryCode: addressToEdit?.country_code || metadata.country_code || "",
      deliveryLocationType: metadata.delivery_location_type || "",
      deliveryFloor: metadata.delivery_floor || "",
      deliveryHasElevator: fromBoolean(metadata.delivery_has_elevator),
      deliveryInstructions: metadata.delivery_instructions || metadata.message || "",
      deliveryElevatorDimensions: metadata.delivery_elevator_dimensions || "",
      deliveryElevatorDoorWidth: metadata.delivery_elevator_door_width || "",
      deliveryHasStairs: fromBoolean(metadata.delivery_has_stairs),
      deliveryStairsCount: metadata.delivery_stairs_count || "",
      deliveryHasGroundLevelAccess: fromBoolean(metadata.delivery_has_ground_level_access),
      deliveryAccessDetails: metadata.delivery_access_details || "",
      deliveryParkingType: metadata.delivery_parking_type || "",
      deliveryParkingDetails: metadata.delivery_parking_details || "",
      deliveryContactName: metadata.delivery_contact_name || "",
      deliveryContactPhone: metadata.delivery_contact_phone || "",
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = form;

  const { shippingAddress } = getDifferentAddress(customer?.addresses);
  const stepNumber = currentStep + 1;
  const isLastStep = currentStep === flowSteps.length - 1;
  const deliveryHasElevator = watch("deliveryHasElevator");
  const deliveryHasStairs = watch("deliveryHasStairs");
  const deliveryHasGroundLevelAccess = watch("deliveryHasGroundLevelAccess");
  const deliveryParkingType = watch("deliveryParkingType");

  const onSubmit = (data: ShippingAddressSchema) => {
    if (!customer) return toast.warning({ title: __("Login to add an address", locale) });

    if (addressSource === "company" && !companyId) {
      toast.error({
        title: __("Company not found", locale),
        description: __("Please try again later.", locale),
      });
      return;
    }

    if (!addressId && addressSource === "customer") {
      if (shippingAddress && shippingAddress.length >= 5) {
        toast.warning({
          title: __("You have reached the maximum number of shipping addresses allowed.", locale),
          description: __("Please delete an existing address before adding a new one.", locale),
        });

        return;
      }
    }

    startTransition(async () => {
      try {
        let updatedCustomer: StoreCustomer | undefined;
        const deliveryInstructions = data.deliveryInstructions || data.message || "";
        const countryCode = data.countryCode || "be";
        const deliveryMetadata = normalizeDeliverySchedule({
          message: deliveryInstructions,
          type: "shipping",
          address_name: data.addressName,
          country_code: countryCode,
          delivery_location_type: data.deliveryLocationType || undefined,
          delivery_floor: data.deliveryFloor || undefined,
          delivery_has_elevator: toBoolean(data.deliveryHasElevator),
          delivery_instructions: deliveryInstructions || undefined,
          delivery_elevator_dimensions: data.deliveryElevatorDimensions || undefined,
          delivery_elevator_door_width: data.deliveryElevatorDoorWidth || undefined,
          delivery_has_stairs: toBoolean(data.deliveryHasStairs),
          delivery_stairs_count: data.deliveryStairsCount || undefined,
          delivery_has_ground_level_access: toBoolean(data.deliveryHasGroundLevelAccess),
          delivery_access_details: data.deliveryAccessDetails || undefined,
          delivery_parking_type: data.deliveryParkingType || undefined,
          delivery_parking_details: data.deliveryParkingDetails || undefined,
          delivery_contact_name: data.deliveryContactName || undefined,
          delivery_contact_phone: data.deliveryContactPhone || undefined,
        });

        if (addressSource === "company") {
          const endpoint = addressId
            ? `store/companies/${companyId}/addresses/${addressId}`
            : `store/companies/${companyId}/addresses`;

          await sdk.client.fetch(endpoint, {
            method: "POST",
            body: {
              address_name: data.addressName,
              address_1: data.postalAddress,
              city: data.city,
              postal_code: data.postalCode,
              country_code: countryCode,
              company_name: customer.company_name || "",
              first_name: customer.first_name || "",
              last_name: customer.last_name || "",
              phone: customer.phone || "",
              address_type: "shipping",
              metadata: deliveryMetadata,
            },
          });

          if (companyId) {
            queryClient.invalidateQueries({ queryKey: companyAddressesQueryKey(companyId) });
          }
          toast.success({
            title: __(addressId ? "Address updated successfully" : "Shipping address added successfully", locale),
          });
        } else if (addressId) {
          const res = await sdk.store.customer.updateAddress(addressId, {
            address_name: data.addressName,
            address_1: data.postalAddress,
            city: data.city,
            postal_code: data.postalCode,
            metadata: deliveryMetadata,
          });
          updatedCustomer = res.customer;
          toast.success({ title: __("Address updated successfully", locale) });
        } else {
          const res = await sdk.store.customer.createAddress({
            address_name: data.addressName,
            address_1: data.postalAddress,
            city: data.city,
            postal_code: data.postalCode,
            metadata: deliveryMetadata,
          });
          updatedCustomer = res.customer;
          toast.success({ title: __("Shipping address added successfully", locale) });
        }

        if (updatedCustomer) {
          setCustomer(updatedCustomer);
        }
        reset({
          addressName: "",
          postalCode: "",
          city: "",
          postalAddress: "",
          message: "",
          countryCode: "",
          deliveryLocationType: "",
          deliveryFloor: "",
          deliveryHasElevator: undefined,
          deliveryInstructions: "",
          deliveryElevatorDimensions: "",
          deliveryElevatorDoorWidth: "",
          deliveryHasStairs: undefined,
          deliveryStairsCount: "",
          deliveryHasGroundLevelAccess: undefined,
          deliveryAccessDetails: "",
          deliveryParkingType: "",
          deliveryParkingDetails: "",
          deliveryContactName: "",
          deliveryContactPhone: "",
        });
        setCurrentStep(0);
        onSuccess();
      } catch (error) {
        toast.error({
          title: __("Error adding shipping address", locale),
          description: __("Please try again later.", locale),
        });
        console.error("Error saving shipping address:", error);
      }
    });
  };

  const handleAddressAutocomplete = (value: string) => {
    if (!window.google || !value) {
      setPredictions([]);
      return;
    }

    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      {
        input: value,
        componentRestrictions: { country: ["be"] },
        types: ["geocode"],
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setPredictions(predictions);
          setShowDropdown(predictions.length > 0);
        } else {
          setPredictions([]);
        }
      }
    );
  };

  const handlePredictionSelect = (placeId: string, description: string) => {
    if (!window.google) return;

    const placesService = new window.google.maps.places.PlacesService(document.createElement("div"));

    placesService.getDetails(
      {
        placeId,
        fields: ["address_components", "geometry"],
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          console.error("Place details error", status);
          return;
        }

        const components: Record<string, string> = {};

        place.address_components?.forEach((component) => {
          const types = component.types;
          if (types.includes("postal_code")) {
            components.postal_code = component.long_name;
          }
          if (types.includes("locality")) {
            components.city = component.long_name;
          }
          if (types.includes("route") || types.includes("street_address")) {
            components.address_1 = component.long_name;
          }
          if (types.includes("country")) {
            components.country_code = component.short_name;
          }
        });

        setValue("postalCode", components.postal_code || "");
        setValue("city", components.city || "");
        setValue("postalAddress", description);
        setValue("countryCode", components.country_code.toLowerCase() || "");
        setPredictions([]);
      }
    );
  };

  const goToNextStep = async () => {
    const isStepValid = await trigger(flowSteps[currentStep].fields, { shouldFocus: true });
    if (isStepValid) {
      setCurrentStep(Math.min(currentStep + 1, flowSteps.length - 1));
    }
  };

  const goToPreviousStep = () => {
    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit, (fieldErrors) => {
            const invalidStep = flowSteps.findIndex((step) => step.fields.some((field) => fieldErrors[field]));
            if (invalidStep !== -1) {
              setCurrentStep(invalidStep);
            }
          })(e);
        }}
        className="border-primary-light/50 mx-5 border-t pb-5.5"
      >
        <div className="flex flex-col gap-4 py-5">
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={goToPreviousStep}
              disabled={currentStep === 0}
              className="text-blue-brand flex items-center gap-2 text-sm font-medium disabled:invisible sm:text-base"
            >
              <ChevronLeft size={20} aria-hidden="true" />
              {__("Return to the previous step", locale)}
            </button>
            <p className="text-blue-brand shrink-0 text-sm font-medium sm:text-base">
              {stepNumber}/{flowSteps.length}
            </p>
          </div>
          <div className="border-primary-light/30 border-b pb-4">
            <h3 className="font-cabinet-grotesk text-3xl leading-none font-medium">
              {__(flowSteps[currentStep].title, locale)}
            </h3>
          </div>
        </div>

        {currentStep === 0 && (
          <div className="space-y-5">
            <FormField
              control={control}
              name="deliveryLocationType"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">{__("Location type", locale)}</FormLabel>
                    <Select disabled={isPending} value={field.value || ""} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="border-border-light focus-visible:border-primary-light focus-visible:ring-primary-light/50 h-12 bg-white focus-visible:ring-[2px]">
                          <SelectValue placeholder={__("Select a location type", locale)} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {deliveryLocationOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {__(option.label, locale)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="addressName"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">
                      {__("Address name", locale)} <span className="text-pink-primary">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input disabled={isPending} className={inputClassName} {...field} />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="postalAddress"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <div className="relative flex flex-col gap-2.5" ref={dropdownRef}>
                    <FormLabel className="mb-0 text-base font-medium">
                      {__("Postal Address", locale)} <span className="text-pink-primary">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={isPending}
                        className={inputClassName}
                        name={field.name}
                        onBlur={field.onBlur}
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                          handleAddressAutocomplete(e.target.value);
                        }}
                        onFocus={() => {
                          if (predictions.length > 0) {
                            setShowDropdown(true);
                          }
                        }}
                      />
                    </FormControl>
                    {showDropdown && predictions.length > 0 && (
                      <ul className="border-border-light absolute top-full right-0 left-0 z-50 mt-1 max-h-60 overflow-auto rounded border bg-white shadow-lg">
                        {predictions.map((prediction) => (
                          <li
                            key={prediction.place_id}
                            className="cursor-pointer px-4 py-2 text-sm hover:bg-gray-100"
                            onClick={() => handlePredictionSelect(prediction.place_id, prediction.description)}
                          >
                            {prediction.description}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex items-center gap-2.5">
              <FormField
                control={control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem className={cn("w-full gap-1", errors.city && !errors.postalCode && "mb-6")}>
                    <div className="flex flex-col gap-2.5">
                      <FormLabel className="mb-0 text-base font-medium">
                        {__("ZIP Code", locale)} <span className="text-pink-primary">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input disabled={isPending} className={inputClassName} {...field} />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="city"
                render={({ field }) => (
                  <FormItem className={cn("w-full gap-1", errors.postalCode && !errors.city && "mb-6")}>
                    <div className="flex flex-col gap-2.5">
                      <FormLabel className="mb-0 text-base font-medium">
                        {__("City", locale)} <span className="text-pink-primary">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input disabled={isPending} className={inputClassName} {...field} />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        )}

        {currentStep === 1 && (
          <div className="space-y-5">
            <FormField
              control={control}
              name="deliveryHasElevator"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="font-cabinet-grotesk mb-0 text-2xl font-medium">
                      {__("Elevator available?", locale)}
                    </FormLabel>
                    <RadioButton
                      disabled={isPending}
                      name="delivery-has-elevator"
                      defaultValue={field.value}
                      onValueChange={field.onChange}
                      options={[
                        { value: "yes", label: __("Yes", locale) },
                        { value: "no", label: __("No", locale) },
                      ]}
                    />
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {deliveryHasElevator === "yes" && (
              <div className="space-y-5">
                <FormField
                  control={control}
                  name="deliveryElevatorDimensions"
                  render={({ field }) => (
                    <FormItem className="gap-1">
                      <div className="flex flex-col gap-2.5">
                        <FormLabel className="mb-0 text-base font-medium">
                          {__("Elevator dimensions (cm)", locale)}
                        </FormLabel>
                        <FormControl>
                          <Input
                            disabled={isPending}
                            className={inputClassName}
                            placeholder={__("Width x depth x height (if known)", locale)}
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="deliveryElevatorDoorWidth"
                  render={({ field }) => (
                    <FormItem className="gap-1">
                      <div className="flex flex-col gap-2.5">
                        <FormLabel className="mb-0 text-base font-medium">
                          {__("Elevator door width (cm)", locale)}
                        </FormLabel>
                        <FormControl>
                          <Input
                            disabled={isPending}
                            className={inputClassName}
                            placeholder={__("Example: 120cm", locale)}
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
            <FormField
              control={control}
              name="deliveryHasStairs"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="font-cabinet-grotesk mb-0 text-2xl font-medium">
                      {__("Stairs to climb?", locale)}
                    </FormLabel>
                    <RadioButton
                      disabled={isPending}
                      name="delivery-has-stairs"
                      defaultValue={field.value}
                      onValueChange={field.onChange}
                      options={[
                        { value: "yes", label: __("Yes", locale) },
                        { value: "no", label: __("No", locale) },
                      ]}
                    />
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {deliveryHasStairs === "yes" && (
              <FormField
                control={control}
                name="deliveryStairsCount"
                render={({ field }) => (
                  <FormItem className="gap-1">
                    <div className="flex flex-col gap-2.5">
                      <FormLabel className="mb-0 text-base font-medium">
                        {__("Number of stairs to climb", locale)}
                      </FormLabel>
                      <FormControl>
                        <Input
                          disabled={isPending}
                          className={inputClassName}
                          placeholder={__("Example: 1, 3, 6...", locale)}
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={control}
              name="deliveryHasGroundLevelAccess"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="font-cabinet-grotesk mb-0 text-2xl font-medium">
                      {__("Ground-level access?", locale)}
                    </FormLabel>
                    <RadioButton
                      disabled={isPending}
                      name="delivery-ground-level"
                      defaultValue={field.value}
                      onValueChange={field.onChange}
                      options={[
                        { value: "yes", label: __("Yes", locale) },
                        { value: "no", label: __("No", locale) },
                      ]}
                    />
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {deliveryHasGroundLevelAccess === "no" && (
              <FormField
                control={control}
                name="deliveryAccessDetails"
                render={({ field }) => (
                  <FormItem className="gap-1">
                    <div className="flex flex-col gap-2.5">
                      <FormLabel className="mb-0 text-base font-medium">
                        {__("Can you specify the access if it is not ground-level?", locale)}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          disabled={isPending}
                          className={textAreaClassName}
                          placeholder={__("Narrow corridor, tight turn, intermediate landing, heavy door...", locale)}
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <div className="text-blue-brand flex gap-2.5 rounded-sm bg-[#0093D012] px-3 py-4 text-sm leading-[19px]">
              <Info className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
              <p>{__("Our drivers do not carry items upstairs without an elevator.", locale)}</p>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-5">
            <p className="font-cabinet-grotesk text-2xl leading-tight font-medium">
              {__("Where can our truck park on the day?", locale)}
            </p>
            <FormField
              control={control}
              name="deliveryParkingType"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <RadioShipping
                    locale={locale}
                    name="delivery-parking-type"
                    defaultValue={field.value}
                    onValueChange={field.onChange}
                    className="max-w-none gap-4"
                    items={parkingOptions.map((option) => ({
                      value: option.value,
                      content: { title: __(option.title, locale) },
                    }))}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            {["private_parking", "complicated", "special_case"].includes(deliveryParkingType || "") && (
              <FormField
                control={control}
                name="deliveryParkingDetails"
                render={({ field }) => (
                  <FormItem className="gap-1">
                    <div className="flex flex-col gap-2.5">
                      <FormLabel className="mb-0 text-base font-medium">
                        {__("Tell us where the truck can park", locale)}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          disabled={isPending}
                          className={textAreaClassName}
                          placeholder={__("Private gate, loading bay, street constraint...", locale)}
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        )}

        {currentStep === 3 && (
          <p className="border-border-light rounded-sm border bg-white px-4 py-3 text-base font-semibold">
            {FIXED_DELIVERY_HOURS}
          </p>
        )}

        {currentStep === 4 && (
          <div className="space-y-5">
            <FormField
              control={control}
              name="deliveryContactName"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">{__("Contact person name", locale)}</FormLabel>
                    <FormControl>
                      <Input disabled={isPending} className={inputClassName} {...field} />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="deliveryContactPhone"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">{__("Phone", locale)}</FormLabel>
                    <FormControl>
                      <Input disabled={isPending} className={inputClassName} {...field} />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="text-blue-brand flex gap-2.5 rounded-sm bg-[#0093D012] px-3 py-4 text-sm leading-[19px]">
              <Info className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
              <p>{__("A reachable person must be on site before delivery.", locale)}</p>
            </div>
          </div>
        )}

        {currentStep === 5 && (
          <div className="space-y-5">
            <FormField
              control={control}
              name="deliveryFloor"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">{__("Delivery floor", locale)}</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isPending}
                        className={inputClassName}
                        placeholder={__("Ground floor, 1st floor...", locale)}
                        {...field}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="deliveryInstructions"
              render={({ field }) => (
                <FormItem className="w-full gap-1">
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">{__("Additional details", locale)}</FormLabel>
                    <FormControl>
                      <Textarea
                        disabled={isPending}
                        className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light min-h-[157px] resize-none bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                        placeholder={__(
                          "Add any useful delivery details here (access code, floor number, landmarks...).",
                          locale
                        )}
                        {...field}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        <div className="border-primary-light/50 mt-10 flex flex-col gap-3 border-t pt-4">
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={goToPreviousStep}
              disabled={currentStep === 0}
              className="text-blue-brand flex items-center gap-2 text-sm font-medium disabled:invisible sm:text-base"
            >
              <ChevronLeft size={20} aria-hidden="true" />
              {__("Return to the previous step", locale)}
            </button>
            <p className="text-blue-brand shrink-0 text-sm font-medium sm:text-base">
              {stepNumber}/{flowSteps.length}
            </p>
          </div>
          <Button
            size="md"
            disabled={isPending}
            variant={isLastStep ? "rounded" : "primary"}
            className="w-full text-base font-semibold"
            type={isLastStep ? "submit" : "button"}
            onClick={isLastStep ? undefined : goToNextStep}
          >
            {isPending && <Loader2 className="mr-2 animate-spin" aria-hidden="true" />}
            {__(isLastStep ? "Save" : "Next step", locale)}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default ShippingAddressForm;
